import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Button, EmptyState, FormSection, OptionRow, Skeleton, useToast } from '@aps/ui';
import { useSettings } from '../app/settings.js';
import { canOrder } from '../app/session.js';
import { api, ApiError } from '../lib/api.js';

/**
 * Garaj.
 *
 * Marka → model → yil, faqat SELECT orqali. VIN yo'q, chunki moslik
 * jadvali ham yo'q: moslik `searchTags` orqali aniqlanadi va bu
 * EHTIMOLLIK (docs/02-MAHSULOT.md → "Garaj").
 *
 * Yil ro'yxati MODELNING ishlab chiqarish oralig'idan quriladi —
 * mavjud bo'lmagan yilni tanlash imkoniyati bo'lmaydi.
 */
export function GarageScreen(): ReactNode {
  const { t, locale } = useSettings();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [makeId, setMakeId] = useState('');
  const [modelId, setModelId] = useState('');
  const [year, setYear] = useState('');
  const [error, setError] = useState<string | null>(null);

  const vehicles = useQuery({
    queryKey: ['garage'],
    queryFn: api.garage,
    retry: false,
    enabled: canOrder(),
  });
  const makes = useQuery({
    queryKey: ['vehicle-makes'],
    queryFn: api.vehicleMakes,
    staleTime: 600_000,
  });
  const models = useQuery({
    queryKey: ['vehicle-models', makeId],
    queryFn: () => api.vehicleModels(makeId),
    enabled: makeId !== '',
    staleTime: 600_000,
  });

  const selectedModel = (models.data ?? []).find((model) => model.id === modelId);
  const years =
    selectedModel === undefined
      ? []
      : Array.from(
          { length: selectedModel.yearTo - selectedModel.yearFrom + 1 },
          (_, index) => selectedModel.yearTo - index,
        );

  const save = (next: () => Promise<unknown>): void => {
    setError(null);
    next()
      .then(() => queryClient.invalidateQueries({ queryKey: ['garage'] }))
      .catch((caught: unknown) => {
        setError(caught instanceof ApiError ? caught.message_(locale) : t('common.error'));
      });
  };

  const addVehicle = useMutation({
    mutationFn: () => api.addVehicle(modelId, Number(year)),
    onSuccess: (list) => {
      queryClient.setQueryData(['garage'], list);
      setModelId('');
      setYear('');
      setError(null);
      /*
       * Forma TOZALANADI va ro'yxat yangilanadi — ikkalasi ham
       * ko'zdan qochishi mumkin. Xatoda toast YO'Q: xato allaqachon
       * forma yonida matn bilan turibdi va ikki joyda takrorlansa
       * u shovqinga aylanadi.
       */
      toast.show(t('toast.garageSaved'), 'success');
    },
    onError: (caught: unknown) => {
      setError(caught instanceof ApiError ? caught.message_(locale) : t('common.error'));
    },
  });

  return (
    <div className="aps-screen">
      <h1 className="aps-screen__title">{t('garage.title')}</h1>

      {vehicles.isPending ? (
        <Skeleton height="72px" />
      ) : (vehicles.data ?? []).length === 0 ? (
        <EmptyState title={t('garage.empty')} hint={t('garage.emptyHint')} />
      ) : (
        <div className="aps-stack">
          {(vehicles.data ?? []).map((vehicle) => (
            <OptionRow
              key={vehicle.id}
              label={`${vehicle.makeName} ${vehicle.modelName} ${String(vehicle.year)}`}
              selected={vehicle.isActive}
              onSelect={() => {
                save(() => api.setActiveVehicle(vehicle.id));
              }}
              trailing={
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    save(() => api.removeVehicle(vehicle.id));
                  }}
                >
                  {t('garage.remove')}
                </Button>
              }
            />
          ))}
        </div>
      )}

      <FormSection title={t('garage.add')}>
        <select
          className="aps-search__input"
          aria-label={t('garage.make')}
          value={makeId}
          onChange={(event) => {
            setMakeId(event.target.value);
            setModelId('');
            setYear('');
          }}
        >
          <option value="">{t('garage.make')}</option>
          {(makes.data ?? []).map((make) => (
            <option key={make.id} value={make.id}>
              {make.name}
            </option>
          ))}
        </select>

        <select
          className="aps-search__input"
          aria-label={t('garage.model')}
          value={modelId}
          disabled={makeId === ''}
          onChange={(event) => {
            setModelId(event.target.value);
            setYear('');
          }}
        >
          <option value="">{t('garage.model')}</option>
          {(models.data ?? []).map((model) => (
            <option key={model.id} value={model.id}>
              {model.name}
            </option>
          ))}
        </select>

        <select
          className="aps-search__input"
          aria-label={t('garage.year')}
          value={year}
          disabled={modelId === ''}
          onChange={(event) => {
            setYear(event.target.value);
          }}
        >
          <option value="">{t('garage.year')}</option>
          {years.map((value) => (
            <option key={value} value={String(value)}>
              {value}
            </option>
          ))}
        </select>

        {error === null ? null : <p className="aps-stock aps-stock--out">{error}</p>}

        <Button
          block
          disabled={modelId === '' || year === '' || addVehicle.isPending}
          onClick={() => {
            addVehicle.mutate();
          }}
        >
          {t('garage.add')}
        </Button>
      </FormSection>
    </div>
  );
}
